import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { Cross } from '@sravni/react-icons';
import { usePositioner, useStyles, error, useMergedRef, useOutsideClick, useKeyPress } from '@sravni/react-utils';
import { Z_INDEXES } from '../../../constants/zIndexes.js';
import { Icon } from '../../Icon/src/Icon.js';
import { Portal } from '../../Portal/src/Portal.js';
import { Sheet } from '../../Sheet/index.js';
import { Space } from '../../Space/src/Space.js';
import { EASINGS } from '../../Transition/src/_utils.js';
import { fadeConfig } from '../../Transition/src/Fade.js';
import '../../Transition/src/ScaleFade.js';
import '../../Transition/src/SlideFade.js';
import '../../Transition/src/Collapse.js';
import modules_f92dd299 from '../styles/Popover.module.less.js';

const backdropMotionProps = (visible) => ({
    variants: {
        exit: {
            opacity: 0,
            transition: {
                duration: 0.15,
                ease: EASINGS.easeOut,
            },
        },
        enter: {
            opacity: 0.5,
            transition: {
                duration: 0.2,
                ease: EASINGS.easeIn,
            },
        },
    },
    animate: visible ? 'enter' : 'exit',
    initial: 'exit',
    exit: 'exit',
});
const popoverMotionProps = (visible) => ({
    ...fadeConfig,
    animate: visible ? 'enter' : 'exit',
});
const defaultPositionerSettings = {
    matchWidth: false,
    placement: 'bottom-start',
    flip: true,
    offset: [0, 4],
};
/**
 * @description The component can show arbitrary content in the popup window (desktop mode) or sheet (mobile mode).
 *
 * @component
 * @example
 * ```jsx
 * <Popover content={<Component/>}>triggerElement</Popover>
 * ```
 */
const Popover = React.forwardRef((props, ref) => {
    const { visible, header, content, footer, backdrop, mobile, closable, fullscreen, positionerSettings = defaultPositionerSettings, className, children, onClose, ...restProps } = props;
    const reference = React.useRef(null);
    const popper = React.useRef(null);
    const { referenceRef, getPopperProps } = usePositioner({
        ...defaultPositionerSettings,
        ...positionerSettings,
    });
    const cx = useStyles(modules_f92dd299);
    const containerClassNames = cx(className, 'container');
    let triggerElement = null;
    const popoverVisible = !!visible && (!!header || !!content || !!footer);
    const withBackdrop = popoverVisible && !!backdrop;
    // Ensure Popover has only one child node
    const count = React.useMemo(() => React.Children.count(children), [children]);
    const condition = count === 0 || count > 1;
    if (condition) {
        error({
            condition,
            message: 'Ensure Popover has only one child node',
        })();
        triggerElement = children;
    }
    else {
        const child = React.Children.only(children);
        const triggerClassNames = cx(child.props.className, {
            triggerBackdrop: withBackdrop,
        });
        const desktopProps = {
            ...child.props,
            className: triggerClassNames,
            // eslint-disable-next-line react-hooks/rules-of-hooks
            ref: useMergedRef(child.ref, reference, referenceRef),
        };
        triggerElement = mobile ? React.cloneElement(child, child.props) : React.cloneElement(child, desktopProps);
    }
    const desktopView = React.useMemo(() => (React.createElement(Portal, null,
        React.createElement(AnimatePresence, null, popoverVisible && (React.createElement("div", { className: cx({ overlay: withBackdrop }) },
            withBackdrop && React.createElement(motion.div, { ...backdropMotionProps(popoverVisible), className: cx('backdrop') }),
            React.createElement(motion.div, { ...popoverMotionProps(popoverVisible), ...getPopperProps({ style: { zIndex: Z_INDEXES.DROPDOWN } }, popper) },
                React.createElement(Space, { ref: ref, "data-qa": "Popover", direction: "vertical", size: 16, className: containerClassNames, ...restProps },
                    closable && React.createElement(Icon, { className: cx('close'), size: 24, icon: React.createElement(Cross, null), onClick: onClose }),
                    header && React.createElement("div", { className: cx('header') }, header),
                    content && React.createElement("div", { className: cx('content') }, content),
                    footer && React.createElement("div", null, footer)))))))), [
        popoverVisible,
        cx,
        withBackdrop,
        getPopperProps,
        ref,
        containerClassNames,
        restProps,
        closable,
        onClose,
        header,
        content,
        footer,
    ]);
    const mobileView = React.useMemo(() => (React.createElement(Sheet, { ref: ref, "data-qa": "Popover", className: className, visible: popoverVisible, fullscreen: fullscreen, closable: closable, onClose: onClose, ...restProps },
        header && React.createElement(Sheet.Header, null, header),
        content && React.createElement(Sheet.Content, null, content),
        footer && React.createElement(Sheet.Footer, null, footer))), [ref, popoverVisible, header, content, footer, closable, fullscreen, className, restProps, onClose]);
    const view = React.useMemo(() => (mobile ? mobileView : desktopView), [mobile, desktopView, mobileView]);
    useOutsideClick([reference, popper], () => onClose?.(), popoverVisible);
    useOutsideClick(reference, () => onClose?.(), !popoverVisible);
    useKeyPress({
        enabled: popoverVisible,
        keyMap: {
            Escape: (event) => {
                event.preventDefault();
                onClose?.();
            },
            Tab: () => {
                onClose?.();
            },
        },
    }, [popoverVisible, onClose, onClose]);
    return (React.createElement(React.Fragment, null,
        triggerElement,
        view));
});
Popover.displayName = 'Popover';

export { Popover };
//# sourceMappingURL=Popover.js.map
